import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button } from '@parsina/framework';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { rxRouteKey, levelKey } from '../../constants/vocabulary.js';
import { rxLine, printIdentity } from '../../screens/Visit/Visit.shared.js';

// «چاپ نسخه» — a prescription that must be impossible to mistake for one.
//
// core.md §7.5 fixes three things about this sheet and every one of them is a
// safety property rather than a style choice:
//
//   1. It is WATERMARKED «نسخهٔ تمرینی — فاقد اعتبار», diagonally, across the
//      page, in print as well as on screen. A practice prescription that
//      photocopies cleanly is a prescription somebody eventually presents at a
//      pharmacy counter.
//   2. It carries the STUDENT's identity — name, level, university, student
//      number — because the person who wrote it is a student and the paper
//      should say so.
//   3. It carries NO «کد نظام پزشکی» field, not even an empty one. A blank
//      licence box is an invitation to fill it in, and the one thing this app
//      must never produce is a document that is one handwritten number away from
//      looking valid.
//
// The patient on it is `displayName` — an invented name — and never a national
// id, a phone number or a date of birth, because those columns do not exist
// anywhere in this product (§3.1).

const WATERMARK_ID = 'rx-print-watermark';

export default function PrintPrescription({
  open, onClose, items = [], patient, me, encounter,
}) {
  const { t, formatNumber, formatDate } = useTranslation();
  const identity = printIdentity(me);

  const print = () => {
    if (typeof window !== 'undefined' && typeof window.print === 'function') window.print();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent size="lg" data-testid="rx-print-dialog">
        <DialogHeader>
          <DialogTitle>{t('visit.rx.printTitle')}</DialogTitle>
        </DialogHeader>

        <div
          id="rx-print-area"
          style={{ position: 'relative', padding: 16, background: '#fff', overflow: 'hidden' }}
        >
          {/* The watermark is a real element, not a CSS background: a background
              image is the first thing a browser drops when «Background graphics»
              is unticked in the print dialog. */}
          <div
            id={WATERMARK_ID}
            data-testid="rx-watermark"
            aria-hidden={false}
            style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
              transform: 'rotate(-24deg)', fontSize: 26, fontWeight: 900,
              color: 'rgba(217, 48, 37, 0.18)', letterSpacing: 1, textAlign: 'center',
            }}
          >
            {t('visit.rx.watermark')}
          </div>

          <header style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 10, marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
              {identity.fullName}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: CT.ink2 }}>
              {[
                identity.level ? t(levelKey(identity.level)) : '',
                identity.university,
                identity.studentNumber
                  ? `${t('visit.rx.studentNumber')}: ${identity.studentNumber}`
                  : '',
              ].filter(Boolean).join(' · ')}
            </p>
          </header>

          <section style={{ marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>
              {patient?.displayName || patient?.title || ''}
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: CT.mute }}>
              {[
                encounter?.seq ? t('visit.number', { n: formatNumber(encounter.seq) }) : '',
                encounter?.startedAtMs ? formatDate(Number(encounter.startedAtMs)) : '',
              ].filter(Boolean).join(' · ')}
            </p>
          </section>

          <ol style={{ margin: 0, paddingInlineStart: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((item) => (
              <li key={item.id} style={{ fontSize: 13, lineHeight: 1.9 }}>
                <strong>{item.drugName}</strong>
                {item.route && <span>{` — ${t(rxRouteKey(item.route))}`}</span>}
                {rxLine(item) && <span>{` — ${rxLine(item)}`}</span>}
                {item.quantity ? <span>{` — ${formatNumber(item.quantity)}`}</span> : null}
                {item.instructions && (
                  <div style={{ fontSize: 12, color: CT.ink2 }}>{item.instructions}</div>
                )}
              </li>
            ))}
          </ol>

          <p style={{ marginTop: 16, fontSize: 11.5, color: C.red, fontWeight: 700 }}>
            {t('visit.rx.printFooter')}
          </p>
        </div>

        <DialogFooter>
          <Button onClick={print} data-testid="rx-print-confirm">{t('visit.rx.printAction')}</Button>
          <Button variant="outline" onClick={onClose}>{t('common.actions.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
